import { useEffect, useState } from 'react';

/**
 * A clock that re-renders its caller, for the countdowns on the health page.
 *
 * React has no notion of time passing, so a component that renders "1m 24s" from a fixed
 * timestamp renders it once and then shows a frozen number until something unrelated causes
 * an update. A timer that looks stopped is worse than no timer on a page whose whole purpose
 * is showing that the schedule is running.
 *
 * One interval per caller. Several sources counting down at once therefore run several
 * intervals, which is cheap at this scale and avoids the shared-clock-provider alternative
 * where every card re-renders whenever any card needs to.
 *
 * `intervalMs` is a ceiling on staleness, not a promise: browsers throttle timers in
 * background tabs to once a minute or stop them entirely. The value is read from the system
 * clock on every tick rather than decremented, so a throttled tab shows the correct remaining
 * time the moment it is focused instead of however far a counter got.
 */
export function useNow(intervalMs = 1000): number {
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), intervalMs);
    // Fires immediately as well as on the interval. Remounting after a pause — switching back
    // to this page, say — would otherwise show the mount-time value for up to a full interval.
    setNow(Date.now());
    return () => window.clearInterval(timer);
  }, [intervalMs]);

  return now;
}

/**
 * `1m 24s`, `340ms`, `2h 5m`. For elapsed and remaining times, not timestamps.
 *
 * Milliseconds are shown below a second because that is the range poll durations live in and
 * "0s" for a 340ms poll loses the only interesting thing about it. Above a minute the seconds
 * are kept and below an hour the minutes are, because the comparison a reader makes is
 * against the last run of the same source, where a 20-second difference is the signal.
 */
export function formatDuration(ms: number): string {
  if (!Number.isFinite(ms)) return '—';
  const total = Math.max(0, Math.round(ms));
  if (total < 1000) return `${total}ms`;

  const seconds = Math.floor(total / 1000);
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ${minutes % 60}m`;
  return `${Math.floor(hours / 24)}d ${hours % 24}h`;
}

/**
 * How long until `iso`, in milliseconds, or null when there is no answer.
 *
 * Negative when the moment has passed, which the caller needs to distinguish rather than have
 * clamped: a countdown that has gone negative is a schedule that has not fired, and showing
 * it as "0s" forever hides exactly that.
 */
export function millisUntil(iso: string | null | undefined, now: number): number | null {
  if (!iso) return null;
  const at = new Date(iso).getTime();
  return Number.isNaN(at) ? null : at - now;
}
